import { useState } from 'react'
import { ArrowRightLeft, Bot, CircleHelp, FileText, Lightbulb, MessageSquare, Wrench } from 'lucide-react'
import { formatDateTime, formatTime } from '@/lib/dates'
import type { AgentActivityEntry } from '@/types/agent'
import type { LucideIcon } from 'lucide-react'

const ICONS: Record<string, LucideIcon> = {
  stage: ArrowRightLeft,
  tool: Wrench,
  finding: Lightbulb,
  handoff: ArrowRightLeft,
  draft: FileText,
  question: CircleHelp,
  answer: MessageSquare,
}

// Long runs make a long list; the rail shows the latest of them unless asked for all.
const COLLAPSED_COUNT = 8

function confidenceOf(entry: AgentActivityEntry): number | null {
  const value = entry.meta?.confidence
  // Agent Relay sends confidence as a fraction (0.72), shown as a percentage.
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 100) : null
}

/**
 * What the agent did on the ticket, as Agent Relay summarised it: stage changes, one line per tool call, findings with
 * their confidence, the hand-off, drafts and questions. The rail's counterpart to the old Investigate steps.
 */
export function ActivityTimeline({ activity }: { activity: AgentActivityEntry[] }) {
  const [expanded, setExpanded] = useState(false)
  if (!activity.length) return null
  const shown = expanded ? activity : activity.slice(-COLLAPSED_COUNT)
  const hidden = activity.length - shown.length

  return (
    <section className="mb-5" data-testid="agent-activity">
      <h3 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Agent activity</h3>
      <div className="rounded-etn-card border border-etn-border-hairline bg-etn-surface px-3 py-2.5 shadow-etn-whisper">
        {hidden > 0 && (
          <button type="button" className="mb-2 text-etn-micro font-semibold text-etn-accent hover:underline" onClick={() => setExpanded(true)}>
            Show {hidden} earlier {hidden === 1 ? 'step' : 'steps'}
          </button>
        )}
        <ol className="space-y-2">
          {shown.map((entry, index) => {
            const Icon = ICONS[entry.kind] ?? Bot
            const confidence = confidenceOf(entry)
            const newRun = index > 0 && shown[index - 1].runId !== entry.runId
            return (
              <li key={entry._id} data-testid="agent-activity-entry">
                {newRun && <div className="mb-2 border-t border-etn-border-row pt-2 text-etn-micro text-etn-text-soft">New run, {formatDateTime(entry.createdAt)}</div>}
                <div className="flex gap-2 text-xs text-etn-text-cell">
                  <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-etn-text-soft" aria-hidden />
                  <span className="min-w-0 flex-1 break-words">
                    {entry.text}
                    {confidence !== null && (
                      <span className="ml-1.5 rounded-etn-pill bg-etn-info-subtle px-1.5 py-px text-etn-micro font-semibold text-etn-info-text">{confidence}%</span>
                    )}
                  </span>
                  <time className="shrink-0 tabular-nums text-etn-micro text-etn-text-soft" dateTime={entry.createdAt} title={formatDateTime(entry.createdAt)}>
                    {formatTime(entry.createdAt)}
                  </time>
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
